/**
 * PATH: packages/email-hub/src/feature/reader-header/lanes/laneModel.ts
 * FILE_ID: EMAILHUB-READERHEADER-LANES-MODEL-001
 * PURPOSE: Pure selectors for lane content — identity summary and signal ranking.
 * ROLE IN ARCHITECTURE: Domain selectors feeding the four lane leaves.
 * MAIN EXPORTS: resolveIdentitySummary, selectIntelligenceSignals, INTELLIGENCE_VISIBLE_LIMIT.
 * NON-RESPONSIBILITIES: No rendering, no network, no menu logic.
 * NOTES FOR FUTURE AI: Signal priority is a plan contract (Intelligence lane):
 *   verification code > invoice > provenance > tags > AI summary. Never
 *   usage-sort. Identity predicates are REUSED from SenderIdentityCard —
 *   do not re-derive resolution rules here.
 */
import {
  isResolvedSupplier,
  isSubscriptionSupplier,
  isTeammateSender,
} from '../../components/SenderIdentityCard'
import { senderIdentityFor } from '../../sender-identity'
import type { Email } from '../../hooks/useEmails'

/** Identity states named by the plan's Company lane. */
export type IdentityKind =
  | 'teammate'
  | 'client'
  | 'supplier'
  | 'subscription'
  | 'contact'
  | 'unknown'
  | 'ambiguous'

export interface IdentitySummary {
  kind: IdentityKind
  /** Every role that matched — ambiguous identities list them all. */
  roles: string[]
}

/**
 * Resolve ONE identity summary for the Company lane.
 *
 * Inputs: server-resolved identity fields on the email.
 * Output: a single kind plus the full matched-role list; two or more roles
 * → 'ambiguous' (the lane shows the first role + "+N" overflow).
 * Failure mode: no signals at all → 'unknown' (lane offers Add-to-CRM).
 */
export function resolveIdentitySummary(
  email: Pick<
    Email,
    'is_internal_sender' | 'client_id' | 'contact_id' | 'supplier_id' | 'supplier_kind'
  >,
): IdentitySummary {
  const roles: string[] = []
  if (isTeammateSender(email)) roles.push('Teammate')
  if (email.client_id) roles.push('Client')
  if (isSubscriptionSupplier(email)) roles.push('Subscription')
  else if (isResolvedSupplier(email)) roles.push('Supplier')
  if (email.contact_id && !email.client_id) roles.push('Contact')

  if (roles.length === 0) return { kind: 'unknown', roles }
  if (roles.length > 1) return { kind: 'ambiguous', roles }
  const kind = (
    {
      Teammate: 'teammate',
      Client: 'client',
      Subscription: 'subscription',
      Supplier: 'supplier',
      Contact: 'contact',
    } as const
  )[roles[0] as 'Teammate' | 'Client' | 'Subscription' | 'Supplier' | 'Contact']
  return { kind, roles }
}

/** How many ranked signal groups the Intelligence lane renders inline. */
export const INTELLIGENCE_VISIBLE_LIMIT = 2

export type IntelligenceSignalKind =
  | 'verification'
  | 'invoice'
  | 'provenance'
  | 'tags'
  | 'ai_summary'

export interface IntelligenceSignal {
  kind: IntelligenceSignalKind
  /** Individual signals this group carries (tags count each tag). */
  count: number
}

export interface IntelligenceSelection {
  /** Ranked groups to render inline, at most INTELLIGENCE_VISIBLE_LIMIT. */
  visible: IntelligenceSignal[]
  /** Individual signals hidden behind "+N" (Signals Manager). */
  hiddenCount: number
  /** Total individual signals across every group. */
  totalCount: number
}

/**
 * Rank the email's signals by the plan's fixed priority and split them into
 * inline groups vs the "+N" overflow.
 *
 * Inputs: email + whether an active (undismissed) verification code exists.
 * Output: ordered visible groups and hidden/total individual counts.
 * Failure mode: no signals → empty visible list, zero counts (lane renders
 * its empty affordance).
 */
export function selectIntelligenceSignals(
  email: Email,
  options: { hasVerification: boolean; hasInvoice: boolean },
): IntelligenceSelection {
  const provenance = senderIdentityFor(email)?.signals
  const provenanceCount = (provenance?.relay ? 1 : 0) + (provenance?.automated ? 1 : 0)
  const tagCount = email.tags?.length ?? 0

  const groups: IntelligenceSignal[] = []
  if (options.hasVerification) groups.push({ kind: 'verification', count: 1 })
  if (options.hasInvoice) groups.push({ kind: 'invoice', count: 1 })
  if (provenanceCount > 0) groups.push({ kind: 'provenance', count: provenanceCount })
  if (tagCount > 0) groups.push({ kind: 'tags', count: tagCount })
  if (email.ai_summary?.trim()) groups.push({ kind: 'ai_summary', count: 1 })

  const visible = groups.slice(0, INTELLIGENCE_VISIBLE_LIMIT)
  const totalCount = groups.reduce((sum, group) => sum + group.count, 0)
  const visibleCount = visible.reduce((sum, group) => sum + group.count, 0)
  return { visible, hiddenCount: totalCount - visibleCount, totalCount }
}
